<template>
  <div>
    <v-sheet
      v-if="dataLoading"
      :color="'grey lighten-4'"
      class="pa-3"
    >
      <v-skeleton-loader type="card-heading, table-row-divider">
      </v-skeleton-loader>
    </v-sheet>
    <div v-else>
      <naj-box :title="local.bankruptcy.subRequests.requestApplicantInfo">
        <div>
          <v-row>
            <v-col
              cols="12"
              md="12"
              lg="12"
            >
              <naj-input
                :type-id="formFieldType.text"
                :label="local.bankruptcy.subRequests.requestApplicantName"
                :value.sync="formModel.requestApplicant.applicantName"
                :disabled="true"
              >
              </naj-input>
            </v-col>
          </v-row>
        </div>
      </naj-box>
      <naj-box
        v-if="!isOriginalParty"
        :title="local.bankruptcy.subRequests.originalPartyInfo"
      >
        <div>
          <v-row>
            <v-col
              cols="12"
              md="6"
              lg="3"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :value.sync="formModel.requestApplicant.requestOwnerTypeId"
                :label="local.bankruptcy.subRequests.requestApplicantForm.onBehalfOf"
                :items="allowedRequestOwnerTypes"
                item-value="id"
                item-text="name"
                :validates="`required`"
                :disabled="disableOnBehalfOf || isOriginal"
                @change="requestOwnerTypeChanged"
              >
              </naj-input>
            </v-col>
            <v-col
              v-if="isStakeholder"
              cols="12"
              md="6"
              lg="3"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :value.sync="formModel.requestApplicant.stakeholderTypeId"
                :label="local.bankruptcy.subRequests.requestApplicantForm.stakeholderType"
                :items="allowedStakeholderTypes"
                item-value="id"
                item-text="name"
                :validates="`required`"
                :disabled="disableOnBehalfOf || disableStakeholders || isOriginal"
                @change="stakeholderTypeChanged"
              >
              </naj-input>
            </v-col>
          </v-row>
          <v-row v-if="isStakeholder && formModel.requestApplicant.stakeholderTypeId && !isInterestedParty">
            <v-col
              cols="12"
              md="6"
              lg="6"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :value.sync="formModel.requestApplicant.requestOwnerObjectKey"
                :label="stakeholdersLabel"
                :items="stakeholders"
                item-value="objectKey"
                item-text="partnerName"
                :validates="`required`"
                :disabled="disableOnBehalfOf || !isNewMode || isOriginal"
                @change="stakeholdersIdentityVerified"
              >
              </naj-input>
            </v-col>
          </v-row>
          <v-row v-if="isStakeholder && formModel.requestApplicant.stakeholderTypeId">
            <v-col cols="12">
              <naj-data-table
                no-header
                :headers="stakeholderHeader"
                :loading="loading"
                :items="stakeholderData"
                :disable-items-per-page="true"
                no-margin-bottom
              >
              </naj-data-table>
            </v-col>
          </v-row>
          <v-row v-if="isTrusteeOwnerType && hasMultipleTrustees">
            <v-col
              cols="12"
              md="6"
              lg="6"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :value.sync="formModel.requestApplicant.trusteeIdentityNumber"
                :label="trusteesLabel"
                :items="individualTrustees"
                item-value="identityNumber"
                item-text="name"
                :validates="`required`"
                :disabled="(disableOnBehalfOf && !disableStakeholders) || !isNewMode || isOriginal"
                @change="trusteeChanged"
              >
              </naj-input>
            </v-col>
          </v-row>
          <v-row v-if="isTrusteeOwnerType && selectedTrustee">
            <v-col cols="12">
              <naj-data-table
                no-header
                :headers="personHeader"
                :loading="loading"
                :items="trusteeData"
                :disable-items-per-page="true"
                no-margin-bottom
              >
              </naj-data-table>
            </v-col>
          </v-row>
          <v-row v-if="(isDebtor || isCompetentParty) && formModel.requestApplicant.debtor">
            <v-col cols="12">
              <debtor-form
                :form-model="formModel.requestApplicant.debtor"
                :loading="true"
                :is-table-data="true"
              >
              </debtor-form>
            </v-col>
          </v-row>
          <v-row v-if="formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.creditor
              && formModel.requestApplicant.creditor">
            <v-col cols="12">
              <identity-verification-form
                :form-model="formModel.requestApplicant.creditor"
                :person-title="local.bankruptcy.common.theCreditor"
                :view-mode="true"
                :is-table-data="true"
              ></identity-verification-form>
            </v-col>
          </v-row>
        </div>
      </naj-box>
      <parent-request-info
        v-if="formModel.parentSubRequestObjectKey && formModel.objectKey"
        :parent-judgements="formModel.parentJudgements"
        :parent-sessions="formModel.parentSessions"
      >
      </parent-request-info>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState, mapActions } = createNamespacedHelpers(
  'BankruptcyModule'
);
const { mapState: mapGlobalState, mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
import { socialIdentityTypeEnum, countriesEnum, socialIdTypeEnum, userType, partyTypeEnum } from '@/helpers/enum';
import { requestOwnerTypeEnum, legalCapacitiesEnum, stakeholderTypesEnum, stakeholderTypeNames, partyTypeNames, subRequestType, requestReplyTypes } from '@/modules/bankruptcy/helpers/enum';
import DebtorForm from './debtor-form.vue';
import IdentityVerificationForm from '@/modules/bankruptcy/components/request/identity-verification-form.vue';
import ParentRequestInfo from './parent-request-info.vue';

export default {
  name: 'SubRequestApplicantOnlyName',
  inject: ['$validator'],
  components: {
    DebtorForm,
    IdentityVerificationForm,
    ParentRequestInfo,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    isNewMode: {
      type: Boolean,
      required: true,
    },
    isViewMode: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      dataLoading: null,
      isOriginalParty: null,
      loading: null,
      userType,
      partyTypeEnum,
      legalCapacitiesEnum,
      partyTypeNames,
      stakeholderTypeNames,
      subRequestType,
      requestApplicant: {},
      mainRequestParties: {},
      stakeholderTypesEnum,
      requestOwnerTypeEnum,
      stakeholders: [],
      requestReplyTypes,
      selectedStakeholder: {},
      stakeholdersLabel: '',
      stakeholderHeader: [],
      stakeholderData: [],
      selectedTrustee: null,
      trusteeData: [],
      trusteesLabel: (this.local.bankruptcy.subRequests.addRepresentative.bankruptcyTrustee),
      firmHeader: [
        {
          value: 'organizationName',
          text: this.local.bankruptcy.identityVerificationForm.theName,
        },
        {
          value: 'unifiedNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.unifiedNumber,
        },
        {
          value: 'commercialNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.commercialRegistrationNumber,
        },
      ],
      foreignCompanyHeader:[ {
        value: 'organizationName',
        text: this.local.bankruptcy.identityVerificationForm.theName,
      },
      {
        value: 'commercialNumber',
        text: this.local.bankruptcy.requestDetails.bankruptDebts.commercialRegistrationNumber,
      },
      {
        value: 'managerFullName',
        text: this.local.bankruptcy.identityVerificationForm.managerFullName,
      },
      {
        value: 'managerIdentityNumber',
        text: this.local.bankruptcy.identityVerificationForm.managerIdentityNumber,
      },
      ],
      personHeader: [
        {
          value: 'fullName',
          text: this.local.bankruptcy.identityVerificationForm.theName,
        },
        {
          value: 'identityNumber',
          text: this.local.bankruptcy.requestDetails.partners.identityNumber,
        },
        {
          value: 'identityTypeName',
          text: this.local.bankruptcy.requestDetails.partners.identityType,
        },
      ],
      creditor: {},
      applicantIdentityUser: null,
    };
  },
  computed: {
    ...mapLUTState([
      'mainRequestCategories',
      'allRequestOwnerTypes',
      'legalCapacities',
      'stakeholderTypes',
      'currentUser',
    ]),
    ...mapGlobalState(['userInfo']),
    allowedRequestOwnerTypes() {
      if (this.formModel.mainRequest.isRequestOwner) 
        return this.allRequestOwnerTypes.filter(lc => lc.id != requestOwnerTypeEnum.trustee);
      else if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.creditor) {
        return this.allRequestOwnerTypes.filter(lc => lc.id == requestOwnerTypeEnum.creditor);
      } 
      else if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.competentParty) {
        return  this.allRequestOwnerTypes.filter(lc => lc.id == requestOwnerTypeEnum.competentParty);
      }
      else if (this.isTrusteeObjectionRequest){
        if(this.isTrusteeObjectionRequest){
          return this.allRequestOwnerTypes.filter(lc =>
              lc.id == requestOwnerTypeEnum.debtor ||
              lc.id == requestOwnerTypeEnum.trustee
            ).map((x) => {
              if (x.id == requestOwnerTypeEnum.trustee) {
                x.name = this.local.bankruptcy.subRequests.requestInquiry.behalfHimSelf;
              }
              return x;
            });
        }
      }
      else {
        return  this.allRequestOwnerTypes.filter(lc => 
          lc.id == requestOwnerTypeEnum.stakeholder 
        || lc.id == requestOwnerTypeEnum.debtor 
        || (lc.id == requestOwnerTypeEnum.trustee && (this.individualTrustees || []).length > 0));
      }
    },
    allowedStakeholderTypes() {
      if (this.isInterestedParty)
        return this.stakeholderTypes;
      return this.stakeholderTypes.filter(st =>
        st.id != stakeholderTypesEnum.interestParty);
    },
    isCreditor() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.creditor;
    },
    isOriginal() {
      return this.formModel.isOriginal;
    },
    isPartner() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.partner;
    },
    isInterestedParty() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.interestParty;
    },
    isDebtorEmployee() {
      return this.formModel.requestApplicant.stakeholderTypeId == stakeholderTypesEnum.employee;
    },
    isStakeholder() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder;
    },
    isDebtor() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.debtor;
    },
    isTrusteeApplying() {
      const currentIdentity = this.currentUser?.socialId;
      if (!currentIdentity) return false;
      return (this.individualTrustees || []).some(s => s.identityNumber === currentIdentity);
    },
    isTrusteeOwnerType() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.trustee;
    },
    isCompetentParty() {
      return this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.competentParty;
    },
    isInComplete() {
      return this.formModel.statusId == requestReplyTypes.inComplete;
    },
    disableStakeholders() {
      return this.formModel.hasMultiOriginal;
    },
    isNewAddRepresentativeRequest() {
      return this.isNewMode && subRequestType[this.$route.params.requestType] == subRequestType.AddRepresentative;
    },
    disableOnBehalfOf() {
      return !this.isNewAddRepresentativeRequest && !this.isTrusteeObjectionRequest || this.isViewMode;
    },
    isTrusteeObjectionRequest() {
      return this.subRequestType[this.$route.params.requestType] == subRequestType.VerdictObjectionRequest && this.isTrusteeApplying;
    },
    hasMultipleTrustees() {
      return (this.individualTrustees || []).length > 1;
    },
    individualTrustees() {
      const currentIdentity = this.currentUser?.socialId;
      var individuals = (this.formModel.trustees || [])
        .filter(t => t.identityTypeId === 1);
      if(individuals.some(s => s.identityNumber === currentIdentity) && !this.isViewMode){
        individuals = individuals.filter(t => t.identityNumber === currentIdentity);
      }
      return individuals
    }
  },
  mounted() {
    this.dataLoading = true;
    Promise.all([
      this.getLegalCapacities(),
      this.getStakeholderTypes(),
      this.getAllRequestOwnerTypes(),
    ])
      .then(() => {
        this.loadData();
      });
  },
  created() {
  },
  methods: {
    ...mapGlobalActions(['getUserInfo']),
    ...mapActions([
      'getAllRequestOwnerTypes',
      'getLegalCapacities',
      'getStakeholderTypes',
      'getMainRequestParties',
      'getPartiesByStakeholderType',
      'getMainRequestSecretariesApproved',
      'getParentSessionsBySubRequestObjectKey',
      'getParentJudgementsBySubRequestObjectKey'
    ]),
    loadData() {
      const promises = [
        this.getUserInfo(),
        this.getMainRequestParties(this.formModel.mainRequest.objectKey),
        this.getMainRequestSecretariesApproved(this.formModel.mainRequest.objectKey),
      ];
      if (this.formModel?.parentSubRequestObjectKey && this.formModel?.objectKey) {
        promises.push(
          this.getParentSessionsBySubRequestObjectKey(this.formModel.objectKey),
          this.getParentJudgementsBySubRequestObjectKey(this.formModel.objectKey)
        );
      }
      Promise.all(promises)
        .then((res) => {
          const currentUser = this.getCurrentUserModel();
          this.updateFormModel(res[1]);
          this.setApplicantIdentityUser(currentUser);
          this.$set(this.formModel, 'trustees', res[2]?.data)
          if (this.formModel?.parentSubRequestObjectKey && this.formModel?.objectKey) {
            this.$set(this.formModel, 'parentSessions', res[3]?.data)
            this.$set(this.formModel, 'parentJudgements', res[4]?.data)
          }
          this.autoSelectTrustee();
          if (this.isNewAddRepresentativeRequest && !this.isOriginal && !this.isTrusteeApplying) {
            this.setupNewRepresentative(currentUser, res[1]);
          } else {
            this.setFormModel(currentUser, res);
          }
          if (!this.isOriginal && !this.isViewMode && !this.isTrusteeApplying) {
            this.$set(this.formModel, 'representative', this.getCurrentUserModel());
          }
        });
    },

    updateFormModel(data) {
      this.$set(this.formModel, 'isOriginal', data.data.isOriginal);
      this.$set(this.formModel, 'originalLegalCapacityId', data.data.originalLegalCapacityId);
      this.$set(this.formModel, 'SubRequestTypeId', subRequestType[this.$route.params.requestType]);
    },

    setApplicantIdentityUser(currentUser) {
      this.applicantIdentityUser = this.isNewMode ? currentUser.identityNumber : this.formModel.requestApplicant.identityNumber;
    },

    setupNewRepresentative(currentUser, data) {
      this.requestApplicant = this.formModel.requestApplicant;
      this.$set(this.formModel, 'requestApplicant', {
        ...currentUser,
        applicantName: this.requestApplicant.applicantName || currentUser.applicantName,
        debtor: data.data.debtor,
        creditor: data.data.creditor,
      });
      this.$set(this.formModel, 'representative', currentUser);
      this.dataLoading = false;
    },

    setFormModel(currentUser, res) {
      this.checkUserType(currentUser);
      const isTrustee = this.checkIfTrustee(res[2], currentUser);
      this.updateRequestApplicant(res, currentUser, isTrustee);
      this.autoSelectTrustee();
      this.$set(this.formModel, 'originalIdentityNumber', res[1].data.originalIdentityNumber);
      this.$set(this.formModel, 'hasMultiOriginal', res[1].data.originalIdentitiesCount > 1);

      if (this.isViewMode) {
        if (this.formModel.requestApplicant.requestOwnerTypeId == requestOwnerTypeEnum.stakeholder
          && !this.formModel.requestApplicant.stakeholderTypeId) {
          this.setStakeholderTypeForViewMode();
        }
      }
      if (this.isNewMode) {
        this.setTypeForEditMode(res);
      }
      this.isOriginalParty = this.checkIsOriginalParty();
      if (this.formModel.requestApplicant.stakeholderTypeId) {
        this.stakeholderTypeChanged(this.formModel.requestApplicant.stakeholderTypeId);
      }
      else {
        this.dataLoading = false;
      }

    },

    checkUserType(currentUser) {
      if (!this.isViewMode && currentUser.userType === this.userType.najizOrganization && this.currentUser.loginType != 'governmental') {
        this.toaster.error(this.local.bankruptcy.subRequests.addRepresentative.organizationDenied);
        this.$router.push({ path: `/bankruptcy` });
      }
    },
    autoSelectTrustee() {
      if (!this.isTrusteeOwnerType) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        return;
      }
      const trustees = this.individualTrustees || [];
      if (!trustees.length) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        return;
      }
      if (trustees.length === 1) {
        this.applyTrusteeSelection(trustees[0]);
        return;
      }
      const identityNumber = this.formModel?.requestApplicant?.trustee?.identityNumber;
      const matched = trustees.find(sec => identityNumber && sec.identityNumber == identityNumber) || trustees[0];
      if (matched) {
        this.applyTrusteeSelection(matched);
      }
    },

    checkIsOriginalParty() {
      const isDebtorOriginalParty = this.isDebtorOriginalParty();
      const isCreditorOriginalParty = this.isCreditorOriginalParty();
      return isDebtorOriginalParty || isCreditorOriginalParty;
    },

    isDebtorOriginalParty() {
      const isDebtor = this.formModel.requestApplicant.requestOwnerTypeId === this.requestOwnerTypeEnum.debtor;
      const isBankrupt = this.formModel.originalLegalCapacityId === legalCapacitiesEnum.bankrupt;
      const isApplicantMatch = this.formModel.requestApplicant.debtor?.identityNumber === this.applicantIdentityUser;

      if (this.isNewMode) {
        return isDebtor && isBankrupt && isApplicantMatch;
      } else {
        return isDebtor && isApplicantMatch;
      }

    },

    isCreditorOriginalParty() {
      const isCreditor = this.formModel.requestApplicant.requestOwnerTypeId === this.requestOwnerTypeEnum.creditor;
      const hasLegalCapacity = this.formModel.originalLegalCapacityId === legalCapacitiesEnum.creditor;
      const isApplicantMatch = this.formModel.requestApplicant.creditor?.identityNumber === this.applicantIdentityUser;

      if (this.isNewMode) {
        return isCreditor && hasLegalCapacity && isApplicantMatch;
      } else {
        return isCreditor && isApplicantMatch;
      }

    },

    checkIfTrustee(trusteesData, currentUser) {
      return trusteesData.data.some(x => x.identityNumber === currentUser.identityNumber);
    },

    updateRequestApplicant(res, currentUser, isTrustee) {
      this.requestApplicant = this.formModel.requestApplicant;
      const requestApplicant = {
        applicantName: this.requestApplicant?.applicantName ? this.requestApplicant?.applicantName : currentUser.applicantName,
        applicantDetailsId: this.formModel.applicantDetailsId ? this.formModel.applicantDetailsId : res[1].data.requestApplicant.id,
        debtor: this.requestApplicant.debtor ? this.requestApplicant.debtor : res[1].data.debtor,
        trustee: this.requestApplicant.trustee,
        trusteeIdentityNumber: this.requestApplicant.trustee?.IdentityNumber,
        creditor: this.requestApplicant.creditor ? this.requestApplicant.creditor : res[1].data.creditor,
        attorneyNumber: this.requestApplicant.attorneyNumber,
        attorneyTypeId: this.requestApplicant.attorneyTypeId,
        isAttorneyVerified: !!this.requestApplicant.attorneyNumber,
        requestOwnerTypeId: this.requestApplicant.requestOwnerTypeId ? this.requestApplicant.requestOwnerTypeId
          : isTrustee && this.isNewMode ? this.requestOwnerTypeEnum.debtor : res[1].data.requestOwnerTypeId,
        legalCapacityId: this.requestApplicant.legalCapacityId ? this.requestApplicant.legalCapacityId
          : isTrustee && this.isNewMode ? this.legalCapacitiesEnum.bankruptcySecretary
            : res[1].data.representativeLegalCapacityId ? res[1].data.representativeLegalCapacityId : this.legalCapacitiesEnum.lawyer,
        requestOwnerObjectKey: this.getRequestOwnerObjectKey(),
        partnerId: this.requestApplicant.partner?.id,
        interestedPartyId: this.requestApplicant.interestedParty?.id,
        debtorEmployeeId: this.requestApplicant.debtorEmployee?.id,
        creditorId: this.requestApplicant.creditor?.id,
        bankruptId: res[1].data.debtor.bankruptId,
        stakeholderTypeId: this.requestApplicant.stakeholderTypeId ? this.requestApplicant.stakeholderTypeId
          : this.isNewMode ? res[1].data.stakeholderTypeId : null,
        bankruptDebtId: this.requestApplicant.bankruptDebtId,
        debtorEmployee: this.requestApplicant.debtorEmployee,
        partner: this.requestApplicant.partner,
        interestedParty: this.requestApplicant.interestedParty,
        externalAttorneySourceId: this.requestApplicant.externalAttorneySourceId,
        identityNumber: this.requestApplicant.identityNumber,
        identityTypeId: this.requestApplicant.identityTypeId,
        familyName: this.requestApplicant.familyName,
        fatherName: this.requestApplicant.fatherName,
        thirdName: this.requestApplicant.thirdName,
        firstName: this.requestApplicant.firstName,
        gender: this.requestApplicant.gender,
        isApprovedFromMofa: this.requestApplicant.isApprovedFromMofa,
        isApprovedFromMoj: this.requestApplicant.isApprovedFromMoj,
        mobile: this.requestApplicant.mobile,
        email: this.requestApplicant?.email,
        mojLicenceNumber: this.requestApplicant.mojLicenceNumber,
        nationality: this.requestApplicant.nationality,
        onBehalfOfName: this.requestApplicant.onBehalfOfName,
        secretaryLicenceNumber: this.requestApplicant.secretaryLicenceNumber,

      };
      if (this.isNewMode) {
        this.$set(this.formModel, 'requestApplicant', {
          ...requestApplicant,
          ...currentUser,
        });
      }
      else
        this.$set(this.formModel, 'requestApplicant', requestApplicant);

    },

    getRequestOwnerObjectKey() {
      if (this.isPartner) {
        return this.requestApplicant.partner?.objectKey;
      }
      if (this.isDebtorEmployee) {
        return this.requestApplicant.debtorEmployee?.objectKey;
      }
      if (this.isCreditor) {
        return this.requestApplicant.creditor?.objectKey;
      }
      if (this.isInterestedParty) {
        return this.requestApplicant.interestedParty?.objectKey;
      }
      return '';
    },

    setStakeholderTypeForViewMode() {
      const originalLegalCapacityId = this.formModel.originalLegalCapacityId;
      if (originalLegalCapacityId === legalCapacitiesEnum.creditor) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.creditor);
      } else if (originalLegalCapacityId === legalCapacitiesEnum.partner) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.partner);
      } else if (originalLegalCapacityId === legalCapacitiesEnum.employee) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.employee);
      } else if (originalLegalCapacityId === legalCapacitiesEnum.interestParty) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.interestParty);
      }
    },

    setTypeForEditMode(res) {
      if (this.formModel.mainRequest.isRequestOwner) {
        this.$set(this.formModel, 'applicantDetailsId', res[1].data.requestApplicant.id);
      }
      else if (!this.formModel.mainRequest.isRequestOwner && this.isOriginal) {
        this.$set(this.formModel.requestApplicant, 'legalCapacityId', this.formModel.originalLegalCapacityId);

        if ([legalCapacitiesEnum.bankrupt, legalCapacitiesEnum.debtor, legalCapacitiesEnum.competentParty].includes(this.formModel.originalLegalCapacityId)) {
          this.setOriginalPartyForDebtor();
        }
        else if (this.formModel.requestApplicant.requestOwnerTypeId === this.requestOwnerTypeEnum.creditor) {
          this.setOriginalPartyForCreditor();
        }
        else {
          this.fillStakeholderData(this.formModel.originalLegalCapacityId);
        }
      }
      else if (this.isTrusteeApplying) {
        this.setOriginalPartyForTrustee(this.currentUser);
      }
    },
    setOriginalPartyForTrustee(trustee) {
      let originalParty = {
        ...trustee,
        typeId: trustee.userType,
        identityNumber: trustee.socialId,
        identityTypeId: trustee.socialIdType,
      }
      this.$set(this.formModel, 'originalParty', originalParty);
    },
    setOriginalPartyForDebtor() {
      this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.debtor);
      this.$set(this.formModel.requestApplicant, 'bankruptId', this.formModel.requestApplicant.debtor?.bankruptId);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.debtor);
      this.$set(this.formModel.requestApplicant, 'legalCapacityName', this.legalCapacities.find(lc => lc.id === this.formModel.requestApplicant.legalCapacityId).name);
    },
    setOriginalPartyForCreditor() {
      this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.creditor);
      this.$set(this.formModel.requestApplicant, 'creditorId', this.formModel.requestApplicant.creditor?.creditorId);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.creditor);
      this.$set(this.formModel.requestApplicant, 'legalCapacityName', this.legalCapacities.find(lc => lc.id === this.formModel.requestApplicant.legalCapacityId).name);
    },
    fillStakeholderData(originalLegalCapacityId) {
      this.$set(this.formModel, 'requestApplicant', {
        ...this.formModel.requestApplicant,
        requestOwnerTypeId: this.requestOwnerTypeEnum.stakeholder,
        requestOwnerObjectKey: this.getRequestOwnerObjectKey(),
      });

      let stakeholderType = this.isPartner ? 'partner' : this.isDebtorEmployee ? 'debtorEmployee' : this.isCreditor ? 'creditor' : this.isInterestedParty ? 'interestedParty' : '';

      if (originalLegalCapacityId == legalCapacitiesEnum.creditor) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.creditor);
        stakeholderType = 'creditor';
      }
      else if (originalLegalCapacityId == legalCapacitiesEnum.partner) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.partner);
        stakeholderType = 'partner';
      }
      else if (originalLegalCapacityId == legalCapacitiesEnum.employee) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.employee);
        stakeholderType = 'debtorEmployee';
      }
      else if (originalLegalCapacityId == legalCapacitiesEnum.interestParty) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderTypesEnum.interestParty);
        stakeholderType = 'interestedParty';
      }
      if (stakeholderType != '')
        this.$set(this.formModel.requestApplicant, stakeholderType, this.getCurrentUserModel());
    },
    setCurrenUserInfo() {
      this.$set(this.formModel, 'requestApplicant', {
        ...this.getCurrentUserModel(),
        ...this.formModel.requestApplicant,
      });
      if (!this.isOriginal && !this.isViewMode && !this.isTrusteeApplying) {
        this.$set(this.formModel, 'representative', this.getCurrentUserModel());
      }
    },
    getCurrentUserModel() {
      const names = [
        this.userInfo.firstName,
        this.userInfo.fatherName,
        this.userInfo.grandFatherName,
        this.userInfo.familyName,
      ];
      const fullName = names.filter(n => n).join(' ');
      const socialTypeId = this.userInfo.socialIdType;
      let user = {
        fullName: fullName,
        applicantName: this.isViewMode ? this.requestApplicant.applicantName : fullName,
        isIdentityVerified: true,
        identityTypeId: socialTypeId,
        identityNumber: this.userInfo.socialId,
        firstName: this.userInfo.firstName,
        fatherName: this.userInfo.fatherName,
        thirdName: this.userInfo.grandFatherName,
        familyName: this.userInfo.familyName,
        dateOfBirth: this.userInfo.dateOfBirth,
        nationality: socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId,
        gender: this.userInfo.gender,
        userType: this.userInfo.userType,
      };
      return user;
    },

    requestOwnerTypeChanged(value) {
      this.$set(this.formModel, 'originalParty', {});
      if (this.isDebtor || this.isCompetentParty) {
        this.$set(this.formModel.requestApplicant, 'bankruptId', this.formModel.requestApplicant.debtor.bankruptId);
        this.$set(this.formModel, 'originalParty', this.formModel.requestApplicant.debtor);
        this.$set(this.formModel.requestApplicant, 'legalCapacityId', value);
      }
      else {
        this.$set(this.formModel.requestApplicant, 'bankruptId', null);
      }
      this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', null);
      if (this.isTrusteeOwnerType) {
        this.selectedTrustee = null;
        this.trusteeData = [];
        this.$set(this.formModel.requestApplicant, 'trusteeIdentityNumber', null);
        this.$set(this.formModel.requestApplicant, 'legalCapacityId', this.legalCapacitiesEnum.bankruptcySecretary);
        this.autoSelectTrustee();
      }
    },

    mapLegalCapacityId() {
      if (this.formModel.requestApplicant.identityNumber !== null
        && this.formModel.requestApplicant.identityNumber !== undefined
        && this.formModel.requestApplicant.identityNumber == this.formModel.representative?.identityNumber) {
        this.$set(this.formModel.requestApplicant, 'isIdentityVerified', true);
        this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.lawyer);
      }
      else {
        this.$set(this.formModel.requestApplicant, 'isIdentityVerified', false);
        if (this.isPartner) {
          this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.partner);
        }
        else if (this.isDebtorEmployee) {
          this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.employee);
        }
        else if (this.isCreditor) {
          this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.creditor);
        }
      }
    },
    combineManagerName(manager){
      return `${manager?.firstName || ''} ${manager?.fatherName || ''} ${manager?.thirdName || ''} ${manager?.familyName || ''}`;
    },
    stakeholderTypeChanged(value) {
      this.stakeholderData = [];
      this.stakeholderHeader = [];
      this.mapLegalCapacityId(value);
      if (value) {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', value);
        this.partiesByStakeholderType(value);
        this.setStakeholderLabel(value);
      }
      else {
        this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', null);
      }
    },
    checkIfOriginalCreditor() {
      return this.stakeholders.some(s => s.IdentityNumber == this.formModel.requestApplicant.identityNumber);
    },
    fillStakeholderForNonPartiesData(stakeholderType){
      this.loading = false;
      this.dataLoading = false;
      let holder = null;
      switch (true) {
        case this.isPartner:
          holder = this.formModel.requestApplicant.partner;
          break;
        case this.isDebtorEmployee:
          holder = this.formModel.requestApplicant.debtorEmployee;
          break;
        case this.isInterestedParty:
          holder = this.formModel.requestApplicant.interestedParty;
          break;
        case this.isCreditor:
          holder = this.formModel.requestApplicant.creditor;
          break;
        default:
          holder = this.formModel.originalParty || null;
      }     

      if (!holder) {
        return;
      }

      const isPerson = holder.typeId === partyTypeEnum.person;
      const isForeignCompany = holder.typeId === partyTypeEnum.foreignCompany;

      this.stakeholderHeader = isPerson
        ? this.personHeader
        : (isForeignCompany ? this.foreignCompanyHeader : this.firmHeader);
      this.stakeholderData = [];
      this.stakeholders.push({
            ...holder,
            partnerName: `${holder.fullName || holder.name || holder.organizationName}  (${this.local.bankruptcy.common[partyTypeNames[holder.typeId]]})`,
            employeeName: `${holder.fullName || holder.name || holder.organizationName}`,
            bankruptDebtName: `${holder.fullName || holder.name || holder.organizationName}  (${this.local.bankruptcy.common[partyTypeNames[holder.typeId]]})`,
            fullName: `${holder.fullName || holder.name || holder.organizationName}`,
            identityTypeName: socialIdTypeEnum[holder.identityTypeId],
            id: stakeholderType == stakeholderTypesEnum.partner ? holder.partnerId : stakeholderType == stakeholderTypesEnum.creditor ? holder.creditorId : holder.debtorEmployeeId,
            commercialNumber: `${holder.commercialRegistrationNumber}`,
          });
      this.stakeholderData.push({
        organizationName: holder.organizationName,
        fullName: holder.fullName || holder.name,
        identityNumber: holder.identityNumber,
        unifiedNumber: holder.unifiedNumber,
        identityTypeName: socialIdTypeEnum[holder.identityTypeId],
        managerNameOrIdentityType: this.combineManagerName(holder.manager),
        managerFullName: this.combineManagerName(holder.manager),
        managerIdentityNumber: holder.manager?.identityNumber,
        dateOfBirth: holder.dateOfBirth,
        commercialNumber: `${holder.commercialRegistrationNumber || holder.commercialNumber || ''}`,
      });

      this.setStakeholderLabel(stakeholderType);
      this.isOriginalParty = holder.identityNumber === this.applicantIdentityUser;

      return;      
    },
    partiesByStakeholderType(stakeholderType) {
      this.loading = true;
      
      this.getPartiesByStakeholderType({
        objectKey: this.formModel.mainRequest.objectKey,
        StakeholderTypeId: stakeholderType,
      })
        .then((res) => {
          this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.stakeholder);
          this.$set(this.formModel.requestApplicant, 'stakeholderTypeId', stakeholderType);
          this.stakeholders = res.data ? res.data.map(holder => ({
            ...holder,
            partnerName: `${holder.name}  (${this.local.bankruptcy.common[partyTypeNames[holder.typeId]]})`,
            employeeName: `${holder.name}`,
            bankruptDebtName: `${holder.name}  (${this.local.bankruptcy.common[partyTypeNames[holder.typeId]]})`,
            fullName: `${holder.name}`,
            objectKey: `${holder.objectKey}`,
            identityTypeName: socialIdTypeEnum[holder.identityTypeId],
            id: stakeholderType == stakeholderTypesEnum.partner ? holder.partnerId : stakeholderType == stakeholderTypesEnum.creditor ? holder.creditorId : holder.debtorEmployeeId,
            commercialNumber: `${holder.commercialRegistrationNumber}`,
            pendingApproval: holder.pendingApproval,
          })) : [];
          if(!this.isOriginalParty && !this.isCreditor || !this.checkIfOriginalCreditor() || this.formModel?.parentSubRequestObjectKey){
            this.fillStakeholderForNonPartiesData(stakeholderType);
            return;
          }
          if (this.isNewMode) {
            this.stakeholders = this.stakeholders.filter(holder => holder.pendingApproval != true);
          }

          if (!this.formModel.hasMultiOriginal || this.isViewMode || this.isOriginal || this.isEditMode) {
            let holder = this.stakeholders.find(x =>
              (x.identityNumber !== null && x.identityNumber !== undefined
                && (x.identityNumber == this.applicantIdentityUser || x.identityNumber == this.formModel.originalIdentityNumber))
              || (x.id !== null && x.id !== undefined && x.id == this.stakeholderId(stakeholderType))
              || (x.objectKey !== null && x.objectKey !== undefined && (x.objectKey == this.formModel.requestApplicant.requestOwnerObjectKey || x.objectKey == this.creditor.objectKey))
              || (x.unifiedNumber !== null && x.unifiedNumber !== undefined && x.unifiedNumber == this.formModel.originalIdentityNumber)
              || (x.commercialRegistrationNumber !== null && x.commercialRegistrationNumber !== undefined && x.commercialRegistrationNumber == this.formModel.originalIdentityNumber
              ));
            if (holder) {
              this.$set(this.formModel.requestApplicant, 'requestOwnerObjectKey', holder.objectKey);
              this.selectedStakeholder = holder;
              this.stakeholdersIdentityVerified(this.formModel.requestApplicant.requestOwnerObjectKey);
              this.mapIdentityVerifiedResponse(holder, stakeholderTypeNames[stakeholderType], this.local.bankruptcy.subRequests.addRepresentative.creditorNotFound);
            }
          }
          else {
            this.isOriginalParty = false;
            this.dataLoading = false;
          }
        })
        .finally(() => {
          this.loading = false;
          this.dataLoading = false;
          this.$set(this.formModel.requestApplicant, 'attorneyNumber', this.requestApplicant.attorneyNumber);
          this.$set(this.formModel.requestApplicant, 'attorneyTypeId', this.requestApplicant.attorneyTypeId);
          this.$set(this.formModel.requestApplicant, 'isAttorneyVerified', !!this.requestApplicant.attorneyNumber);
        });
    },
    setStakeholderLabel(holderType) {
      if (holderType == stakeholderTypesEnum.partner)
        this.stakeholdersLabel = this.local.bankruptcy.requestDetails.partners.partnerName;
      else if (holderType == stakeholderTypesEnum.employee)
        this.stakeholdersLabel = this.local.bankruptcy.requestDetails.debtorEmployees.debtorEmployeeName;
      else if (holderType == stakeholderTypesEnum.creditor)
        this.stakeholdersLabel = this.local.bankruptcy.requestDetails.bankruptDebts.bankruptDebtName;
      else if (holderType == stakeholderTypesEnum.interestParty)
        this.stakeholdersLabel = this.local.bankruptcy.requestDetails.interestParty.tabTitle;
    },

    stakeholderId(holderType) {
      if (holderType == stakeholderTypesEnum.partner)
        return this.formModel.requestApplicant.partnerId;
      else if (holderType == stakeholderTypesEnum.employee)
        return this.formModel.requestApplicant.debtorEmployeeId;
      else if (holderType == stakeholderTypesEnum.creditor)
        return this.formModel.requestApplicant.creditorId;
      else if (holderType == stakeholderTypesEnum.interestParty)
        return this.formModel.requestApplicant.interestedPartyId;
    },

    stakeholdersIdentityVerified(value) {
      if (value) {
        this.loading = true;
        let holder = this.stakeholders.find(x => x.objectKey == value);
        if (!holder) {
          let id = this.isPartner ? this.form.requestApplicant.partnerId : this.isDebtorEmployee ? this.formModel.requestApplicant.debtorEmployeeId : this.isInterestedParty ? this.formModel.requestApplicant.interestedPartyId : this.formModel.requestApplicant.creditorId;
          holder = this.stakeholders.find(x => x.partnerId == id || x.debtorEmployeeId == id || x.bankruptDebtId == id || x.interestedPartyId == id);
        }
        this.stakeholderHeader = holder.isPerson ? this.personHeader : this.firmHeader;
        this.stakeholderHeader = holder.isPerson ? this.personHeader : holder.typeId == partyTypeEnum.foreignCompany ? this.foreignCompanyHeader:this.firmHeader;
        if (holder.identityNumber == this.applicantIdentityUser)
          this.isOriginalParty = true;
        else
          this.isOriginalParty = false;

        this.dataLoading = false;

        // if (!this.isOriginalParty) {
        //   this.$set(this.formModel.requestApplicant, 'legalCapacityId', legalCapacitiesEnum.lawyer);
        // }

        this.stakeholderData = [];
        this.stakeholderData.push({
          organizationName: holder.organizationName,
          fullName: holder.fullName,
          identityNumber: holder.identityNumber,
          unifiedNumber: holder.unifiedNumber,
          identityTypeName: holder.identityTypeName,
          managerNameOrIdentityType: holder.managerFullName,
          managerFullName: holder.managerFullName,
          managerIdentityNumber:holder.managerIdentityNumber,
          shareOrBD: holder.share,
          dateOfBirth: holder.dateOfBirth,
          commercialNumber: `${holder.commercialRegistrationNumber}`,
        });
        this.setStakeholderLabel(this.formModel.requestApplicant.stakeholderTypeId);
        this.mapIdentityVerifiedResponse(holder, stakeholderTypeNames[this.formModel.requestApplicant.stakeholderTypeId], this.local.bankruptcy.subRequests.addRepresentative.creditorNotFound);
      }
      this.loading = false;
    },
    mapIdentityVerifiedResponse(response, requestApplicantType, errorMessage) {
      if (response) {
        response.isRetrieved = true;
        this.formModel.requestApplicant.isAttorneyVerified = !!this.formModel.requestApplicant.attorneyNumber;
        this.$set(this.formModel.requestApplicant, 'isAttorneyVerified', !!this.formModel.requestApplicant.attorneyNumber);
        this.$set(this.formModel.requestApplicant, requestApplicantType, response);
        this.$set(this.formModel.requestApplicant, 'isIdentityVerified', true);
        this.$set(this.formModel, 'originalParty', response);
      } else {
        this.toaster.error(errorMessage);
        this.$set(this.formModel.requestApplicant, 'isIdentityVerified', false);
      }

    },
    trusteeChanged(value) {
      if (!value) {
        this.clearTrusteeSelection();
        return;
      }
      this.loading = true;
      const sec = (this.formModel.trustees || []).find(x => x.objectKey == value) ||
        (this.formModel.trustees || []).find(x => x.identityNumber == value);
      if (sec) {
        this.applyTrusteeSelection(sec);
      }
      this.loading = false;
      this.dataLoading = false;
    },
    clearTrusteeSelection() {
      this.selectedTrustee = null;
      this.trusteeData = [];
    },
    applyTrusteeSelection(sec) {
      if (!sec) return;
      this.selectedTrustee = sec;
      this.$set(this.formModel.requestApplicant, 'legalCapacityId', this.legalCapacitiesEnum.bankruptcySecretary);
      this.$set(this.formModel.requestApplicant, 'requestOwnerTypeId', this.requestOwnerTypeEnum.trustee);
      this.$set(this.formModel.requestApplicant, 'trusteeIdentityNumber', sec.identityNumber);
      this.$set(this.formModel.requestApplicant, 'isIdentityVerified', true);
      this.trusteeData = [{
        fullName: sec.name,
        identityNumber: sec.identityNumber,
        identityTypeName: socialIdTypeEnum[sec.identityTypeId],
      }];
      this.mapIdentityVerifiedResponse({...sec, typeId: partyTypeEnum.person}, 'trustee', this.local.bankruptcy.subRequests.addRepresentative.trusteeNotFound);
    },
    async copyURL(videoConferenceUrl) {
      if (videoConferenceUrl) {
        await navigator.clipboard.writeText(videoConferenceUrl);
        this.toaster.success(this.local.home.upcomingSessions.copied);
      }
    },
  },
};
</script>
